import { useEffect, useState } from "react";
import { Star, MessageSquarePlus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { api, SERVICES } from "@/lib/config";

function Stars({ n, onClick }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          type="button"
          key={i}
          onClick={onClick ? () => onClick(i) : undefined}
          className={onClick ? "cursor-pointer" : "cursor-default"}
          data-testid={onClick ? `rating-star-${i}` : undefined}
        >
          <Star className={`w-5 h-5 ${i <= n ? "fill-[#FF4500] text-[#FF4500]" : "text-zinc-300"}`} />
        </button>
      ))}
    </div>
  );
}

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", rating: 5, comment: "", service_used: "" });

  const load = async () => {
    try {
      const { data } = await api.get("/reviews");
      setReviews(data);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.comment) {
      toast.error("Lütfen isim ve yorum alanlarını doldurun.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reviews", form);
      toast.success("Yorumunuz alındı! Onaydan sonra yayınlanacak.");
      setOpen(false);
      setForm({ name: "", rating: 5, comment: "", service_used: "" });
    } catch {
      toast.error("Yorum gönderilemedi.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="reviews" className="py-20 lg:py-32 bg-white" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-6 mb-12">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#1D4ED8] mb-4">
              / Müşteri Yorumları
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter text-zinc-950 leading-[1.05]" data-testid="reviews-title">
              Müşterilerimiz <br />
              <span className="text-[#FF4500]">ne diyor?</span>
            </h2>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-zinc-950 hover:bg-zinc-800 text-white rounded-sm font-bold gap-2 h-11 px-5" data-testid="open-review-dialog-btn">
                <MessageSquarePlus className="w-4 h-4" />
                Yorum Bırak
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md" data-testid="review-dialog">
              <DialogHeader>
                <DialogTitle>Yorumunuzu bırakın</DialogTitle>
                <DialogDescription>
                  Yorumlar onaylandıktan sonra sitede görünür.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Adınız *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="review-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>Puan</Label>
                  <Stars n={form.rating} onClick={(r) => setForm({ ...form, rating: r })} />
                </div>
                <div className="space-y-2">
                  <Label>Aldığınız Hizmet</Label>
                  <Select value={form.service_used} onValueChange={(v) => setForm({ ...form, service_used: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="review-service-select">
                      <SelectValue placeholder="Hizmet seçin (opsiyonel)" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICES.map((s) => (
                        <SelectItem key={s.id} value={s.title}>{s.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Yorumunuz *</Label>
                  <Textarea value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} className="rounded-sm min-h-[100px]" data-testid="review-comment-input" />
                </div>
                <Button type="submit" disabled={submitting} className="w-full bg-[#FF4500] hover:bg-[#E63E00] rounded-sm font-bold gap-2" data-testid="submit-review-btn">
                  {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  Gönder
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-12 text-zinc-500" data-testid="reviews-empty">
            Henüz yorum yok. İlk yorumu siz bırakın!
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {reviews.map((r) => (
              <article
                key={r.id}
                className="p-6 border border-zinc-200 rounded-sm bg-white hover:border-zinc-950 hover:-translate-y-1 transition-all duration-300"
                data-testid={`review-card-${r.id}`}
              >
                <Stars n={r.rating} />
                <p className="mt-4 text-zinc-700 text-sm leading-relaxed">
                  "{r.comment}"
                </p>
                <div className="mt-5 pt-4 border-t border-zinc-200 flex items-center justify-between">
                  <div>
                    <div className="font-bold text-zinc-950 text-sm">{r.name}</div>
                    {r.service_used && (
                      <div className="text-xs uppercase tracking-widest text-zinc-500 font-semibold mt-0.5">
                        {r.service_used}
                      </div>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
